'use client';
import React, { useEffect, useId, useMemo, useState, useContext, useRef } from 'react';
import { Title } from './components/title';
import { gridClassName } from './constants';
import { ShoppingItem } from './components/shopping-item';
import { Alert, Checkbox, GlobalLoading } from '@/shared/components';
import { ShoppingSummary } from './components/shopping-summary';
import { CartSummary } from './components/cart-summary';
import { useQueryCartList } from '@/hooks/cart.hook';
import { Empty } from './components/empty';
import { Trans } from 'react-i18next';
import { useAppStore } from '@/store/app-store';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { EmailPrint } from './components/email-print';
import { useProductStore } from './product-store';
import { useCartStore } from './cart-store';
import { syncPrice, removeSplitCart } from '../utils/price-availability';
import { useIsClient } from '@/hooks/use-is-client';
import { usePriceStore } from '@/store/price-store';
import { redirectOrgPage } from '../utils';
import { checkIfFavorite } from '@/app/utils/check-favorite';
import { CURRENCY, LINKS } from '../utils/constant';
import { usePageTracking } from '@/shared/tracking/use-tracking';
import { usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { CartPromotion } from './components/cart-promotion';
import Link from 'next/link';
import { PriceErrorMessage } from '@/shared/pageComponents/price-error-message';
import { ChildCatalogs } from '@/types/product-detail';
import languageContext from '@/context/language';
import { getContactUsLink } from '@/app/utils/get-contact-us-link';
import { useCountry } from '@/hooks/use-country';
import { FWOPromotion } from './components/fwo-promotion';
import { Promotion } from './components/promotion';

const ShoppingCart = () => {
  const { shipToList, userInfo, initialed, isAnonymous, configByCountry } = useAppStore();
  const { isCN, isKR } = useCountry();
  const { clearAvailabilityByCatalogNumber } = useCartStore();
  const isClient = useIsClient();
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t } = useLang(NAMESPACE.cart);
  const cartStore = useCartStore();
  const productStore = useProductStore();
  const { productPrice, setPromotionInfo, fbpSkuInfo, setFBPSkuInfo, promotionInfo } = usePriceStore();
  const [isIndeterminate, setIsIndeterminate] = useState(false);
  const [checked, setChecked] = useState(false);
  const { data, isFetching, refetch } = useQueryCartList();
  const [tipsOpen, setTipsOpen] = useState(false);
  const [accountErrorTipsOpen, setAccountErrorTipsOpen] = useState(false);
  const [favoriteData, setFavoriteData] = useState<any>(null);
  const [priceFailSkus, setPriceFailSkus] = useState([]);
  const placement = usePlacement();
  const { lang } = useContext(languageContext);
  const [currentPromotionCode, setCurrentPromotionCode] = useState('');
  const [updateQuantity, setUpdateQuantity] = useState(false);
  const [fbpUpdateQuantity, setFBPUpdateQuantity] = useState(false);
  const [initial, setInitial] = useState(true);
  usePageTracking(
    data?.products &&
      data.products.length > 0 && {
        items: [
          {
            key: 'products',
            payload: data?.products.map((product) => {
              return {
                name: product.productTitle,
                catalogId: product.catalogNumber,
              };
            }),
          },
          {
            key: 'productImpressions',
            payload: data?.products.map((product) => {
              return {
                name: product.productTitle,
                catalogId: product.catalogNumber,
                placement: placement,
              };
            }),
          },
        ],
      },
    !isFetching && data && data.products ? true : false
  );

  useEffect(() => {
    setChecked(cartStore.selectedCatalogNumbers.length > 0);
  }, [cartStore.selectedCatalogNumbers]);
  const prevProductsRef = useRef(productStore.products);
  useEffect(() => {
    if (promotionInfo?.promoType === 'FBP' && updateQuantity) {
      const prevProducts = prevProductsRef.current;
      const hasQuantityChanged = productStore.products.some((product, index) => {
        return product.quantity !== prevProducts[index]?.quantity;
      });
      if (hasQuantityChanged) {
        setFBPUpdateQuantity(true);
        setUpdateQuantity(false);
        init(promotionInfo?.promoCode);
      }
    }
    if (promotionInfo?.promoType === 'STP' && updateQuantity) {
      init(promotionInfo?.promoCode);
    }
    prevProductsRef.current = productStore.products;
    setIsIndeterminate(cartStore.selectedCatalogNumbers.length !== productStore.products.length);
  }, [cartStore.selectedCatalogNumbers, productStore.products]);

  useEffect(() => {
    usePriceStore.getState().setPromotionInfo(null);
    return () => {
      usePriceStore.getState().setPromotionError(null);
      clearAvailabilityByCatalogNumber();
    };
  }, []);

  useEffect(() => {
    if (isAnonymous) return;
    if (userInfo.isFullUser) {
      if (shipToList && shipToList.length === 0) {
        setAccountErrorTipsOpen(true);
      } else {
        setAccountErrorTipsOpen(false);
      }
    }
    if (userInfo.isLightUser) {
      setTipsOpen(!isAnonymous && (!shipToList || shipToList.length === 0));
    }
  }, [userInfo, shipToList]);

  useEffect(() => {
    isFetching ? GlobalLoading.show() : GlobalLoading.hide();
  }, [isFetching]);

  useEffect(() => {
    if (data && !isFetching) {
      const products = data?.products || [];
      const cardId = data?.cartId || 0;
      productStore.initProducts(products);
      cartStore.changeCartId(cardId);
    }
  }, [data, isFetching]);

  useEffect(() => {
    if (!initialed) return;
    if (shipToList && shipToList.length > 0 && !cartStore.address) return;
    if (usePriceStore.getState().promotionInfo?.promoCode) return;
    init();
  }, [productStore.products, shipToList, cartStore.address, initialed, usePriceStore.getState().promotionInfo]);

  useEffect(() => {
    if (!currentPromotionCode) return;
    if (fbpUpdateQuantity) return;
    init(currentPromotionCode);
  }, [currentPromotionCode]);

  useEffect(() => {
    if (usePriceStore.getState().promotionInfo?.promoType === 'QTP') {
      init();
      return;
    }
    if (updateQuantity) return;
    if (usePriceStore.getState().promotionInfo?.promoCode && productStore.products.length > 0) {
      setCurrentPromotionCode(usePriceStore.getState().promotionInfo.promoCode);
    }
  }, [productStore.products.length, cartStore.address]);

  useEffect(() => {
    if (usePriceStore.getState().promotionInfo?.promoType === 'QTP' && !productStore.isUpdating) {
      refetch();
    }
  }, [usePriceStore.getState().promotionInfo, productStore.isUpdating]);

  const init = (promoCode?: string) => {
    GlobalLoading.show();
    syncPrice(
      productStore.products.map((e) => {
        return {
          childCatalogNumber: e?.catalogNumber || '',
          webCatalogNumber: e?.webCatalogNumber || '',
          quantity: e?.quantity || 1,
        } as ChildCatalogs;
      }),
      cartStore.address?.accountNumber,
      { promotionCode: promoCode }
    )
      .then((res) => {
        setPriceFailSkus(res.failList);
      })
      .finally(() => {
        setCurrentPromotionCode('');
        setUpdateQuantity(false);
        GlobalLoading.hide();
      });
  };

  const onSelectAll = (checked: boolean) => {
    if (checked) {
      const selectedCatalogNumbers = productStore.products
        .filter((e) => {
          const price = productPrice[e.catalogNumber];
          return e.productStatus !== 'BLOCKED' && price?.priceStatus === 'Orderable';
        })
        .map((e) => e.catalogNumber);
      cartStore.changeSelectedProducts(selectedCatalogNumbers);
    } else {
      cartStore.changeSelectedProducts([]);
    }
  };

  // if has RFQ product without price, show tips, else if has contactUs product, show tips
  const productWarningTipsOpen = useMemo(() => {
    const RFQWithoutPrice = productStore.products.find((p) => {
      const price = productPrice?.[p.catalogNumber];
      return price?.priceStatus === 'RFQ';
    });
    if (RFQWithoutPrice) return true;
    return (
      productStore.products.findIndex((e) => {
        const price = productPrice?.[e.catalogNumber];
        return price?.priceStatus === 'ContactUs';
      }) !== -1
    );
  }, [productStore.products, productPrice]);

  const redirectOrg = () => {
    redirectOrgPage(true);
  };

  const toApplyCode = (code: string) => {
    if (code) {
      setCurrentPromotionCode(code);
    }
  };

  const toClearPromotionInfo = () => {
    setCurrentPromotionCode('');
    setPromotionInfo(null);
    if (fbpSkuInfo && Object.keys(fbpSkuInfo).length > 0) {
      setFBPSkuInfo([]);
      removeSplitCart();
    }
  };

  const isLightUserOrAddressIsPending = useMemo(() => {
    if (isCN || !userInfo || !cartStore?.address) return false;
    if (isKR && userInfo.isLightUser) return true;
    if (isKR && userInfo.isFullUser && !cartStore.address?.isActive) return true;
  }, [cartStore.address, userInfo]);
  const getProductInit = (init) => {
    setInitial(init);
  };
  return (
    <>
      <PriceErrorMessage
        failSkus={!productStore.products.length ? [] : priceFailSkus}
        className="print:!hidden mt-8 w-[calc(100%_-_369px)] lg:w-full md:w-full sm:w-full self-start"
        onClose={() => setPriceFailSkus([])}
      />
      <Alert
        open={isLightUserOrAddressIsPending}
        closeable={false}
        type="warning"
        className="print:!hidden mt-8 w-[calc(100%_-_369px)] lg:w-full md:w-full sm:w-full self-start"
        title={null}
        content={
          <Trans
            t={t}
            i18nKey="pending.user.and.address.pending.tooltip"
            components={[<a className="cursor-pointer" href={LINKS.contactUs(lang)} key="tooltip"></a>]}
          />
        }
      />
      <Alert
        open={cartStore.isFreightVatError}
        closeable={false}
        type="warning"
        className="print:!hidden mt-8 w-[calc(100%_-_369px)] lg:w-full md:w-full sm:w-full self-start"
        title={null}
        content={
          <p className="text-sm">
            <span className="font-bold">{commonT('freight.error')}</span>
          </p>
        }
      />
      <Alert
        open={productWarningTipsOpen}
        closeable={false}
        type="warning"
        className="print:!hidden mt-8 w-[calc(100%_-_369px)] lg:w-full md:w-full sm:w-full self-start"
        title={null}
        content={
          <p className="text-sm">
            <span className="font-bold">{t('cart.warning.tips1')}</span>
            <span className="ml-1">{t('cart.warning.tips2')}</span>
          </p>
        }
      />
      <Alert
        open={tipsOpen}
        closeable={false}
        onClose={() => setTipsOpen(false)}
        type="info"
        className="absolute top-2 right-[393px] z-10 print:!hidden"
        white
        shadow
        title={null}
        content={
          <Trans
            t={t}
            i18nKey="register.continue.tips"
            components={[<a className="cursor-pointer" onClick={redirectOrg} key={useId()}></a>]}
          />
        }
      />
      <Alert
        open={accountErrorTipsOpen}
        onClose={() => setAccountErrorTipsOpen(false)}
        type="info"
        className="absolute top-2 right-0 z-10 print:!hidden"
        white
        shadow
        title={null}
        content={
          <Trans
            t={t}
            i18nKey="account.error.tips"
            components={[<Link href={getContactUsLink(lang)} className="cursor-pointer" key={useId()}></Link>]}
          />
        }
      />
      <div className="grid grid-cols-[1fr_345px] md:grid-cols-1 lg:grid-cols-1 sm:grid-cols-1 gap-x-6 min-h-[400px] w-full">
        <Title />
        <EmailPrint />
        <div className="row-start-2 lg:row-start-3 sm:row-start-3 md:row-start-3 col-span-1 flex-1" id="cart-container">
          <div className=" border border-solid border-gray-450 rounded px-5 flex flex-col">
            <div
              className={`border-0 border-b border-solid border-gray-300 h-[42px] items-center text-sm text-gray-1100 font-bold ${gridClassName}`}
            >
              <Checkbox
                className="color-adjust"
                indeterminate={isIndeterminate}
                checked={checked}
                onChange={onSelectAll}
              />
              <span className="sm:hidden"></span>
              <span>{t('item')}</span>
              <span className="lg:hidden sm:hidden md:hidden">{t('availability')}</span>
              <span className="justify-self-center sm:hidden">{commonT('quantity')}</span>
              <span className="justify-self-end sm:hidden">{t('price', { currency: CURRENCY })}</span>
            </div>
            {isClient && (
              <div className="flex-1 min-h-[208px] flex flex-col">
                {productStore.products.map((product, i) => (
                  <ShoppingItem
                    key={product.catalogNumber + '_' + i}
                    index={i}
                    product={product}
                    updateInit={getProductInit}
                    updateQuantity={() => {
                      setUpdateQuantity(true);
                    }}
                    isFavorite={
                      !isAnonymous
                        ? favoriteData?.find((item) => item.catalogNumber === product.catalogNumber)?.isFavorite
                        : false
                    }
                  />
                ))}
                {productStore.products.length === 0 && <Empty />}
              </div>
            )}
            <ShoppingSummary
              updateQuantity={() => {
                setUpdateQuantity(true);
              }}
            />
          </div>
        </div>
        <div
          className={`w-[345px] flex-shrink-0 h-auto row-start-2 col-span-1 sm:w-full sm:row-start-4 sm:mt-4 md:w-full md:row-start-4 md:mt-4 lg:w-full lg:row-start-4 lg:mt-4 ${
            cartStore.selectedCatalogNumbers.length > 0 ? '' : 'print:hidden'
          }`}
        >
          <CartSummary />
          {/* {!isAnonymous && configByCountry?.isShowFWOInfo && <FWOPromotion />} */}
          <Promotion products={data?.products} init={initial} />
          {!isAnonymous && (
            <CartPromotion
              className="pt-7 shopping-cart-promotion"
              toApplyCode={toApplyCode}
              toClearPromotionInfo={() => toClearPromotionInfo()}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;
